import { useState, useContext, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AuthContext } from '../auth/AuthContext';
import { getDrafts, approveDraft, rejectDraft, bulkDraftAction, getSprintHealth, clearAllDrafts } from '../api/aiApi';
import { getSprints } from '../api/sprintApi';
import toast from 'react-hot-toast';

const ITEM_COLORS = {
  epic: { bg: '#faf5ff', border: '#9333ea', text: '#6b21a8' },
  story: { bg: '#EAF6FD', border: '#2C8FC7', text: '#2C63AE' },
  task: { bg: '#dcfce7', border: '#22c55e', text: '#16a34a' },
};

const PRIORITY_COLORS = {
  High: '#ef4444',
  Medium: '#f59e0b',
  Low: '#22c55e',
};

const AIDraftReview = () => {
  const { sprintId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const focusEpicId = searchParams.get('epicId');
  const qc = useQueryClient();
  const { role } = useContext(AuthContext);
  const isManager = ['admin', 'superadmin', 'manager', 'analyst', 'product', 'product_manager', 'lead'].includes(role);

  const [selectedItems, setSelectedItems] = useState(new Set());
  const [expandedStories, setExpandedStories] = useState(new Set());
  const [expandedEpics, setExpandedEpics] = useState(new Set());
  const [showHealth, setShowHealth] = useState(false);

  // Fetch drafts
  const { data: draftsData, isLoading: draftsLoading } = useQuery({
    queryKey: ['ai-drafts', sprintId],
    queryFn: () => getDrafts(sprintId).then(r => r.data),
    enabled: !!sprintId,
  });

  // Fetch sprint info
  const { data: sprints = [] } = useQuery({
    queryKey: ['sprints'],
    queryFn: () => getSprints().then(r => r.data),
  });

  const sprint = sprints.find(s => s.id === parseInt(sprintId));

  // Fetch health analysis
  const { data: healthData, isLoading: healthLoading, refetch: refetchHealth } = useQuery({
    queryKey: ['sprint-health', sprintId],
    queryFn: () => getSprintHealth(sprintId).then(r => r.data),
    enabled: showHealth && !!sprintId,
  });

  // Mutations
  const invalidateSprintBoard = () => {
    qc.invalidateQueries({ queryKey: ['board'] });
  };

  const approveMutation = useMutation({
    mutationFn: approveDraft,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ai-drafts', sprintId] });
      invalidateSprintBoard();
      toast.success('Draft approved and issue created!');
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to approve'),
  });

  const rejectMutation = useMutation({
    mutationFn: rejectDraft,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ai-drafts', sprintId] });
      toast.success('Draft rejected');
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to reject'),
  });

  const bulkMutation = useMutation({
    mutationFn: ({ ids, action }) => bulkDraftAction(ids, action),
    onSuccess: (_, { action }) => {
      qc.invalidateQueries({ queryKey: ['ai-drafts', sprintId] });
      if (action === 'approve') invalidateSprintBoard();
      setSelectedItems(new Set());
      toast.success(`Bulk ${action} completed!`);
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Bulk action failed'),
  });

  const clearAllMutation = useMutation({
    mutationFn: () => clearAllDrafts(sprintId),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['ai-drafts', sprintId] });
      toast.success(`Cleared ${data.data.deleted_count} draft items`);
      navigate('/sprint-board');
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to clear drafts'),
  });

  const handleClearAll = () => {
    if (window.confirm('⚠️ Clear all AI-generated drafts for this sprint? This cannot be undone.')) {
      clearAllMutation.mutate();
    }
  };

  const epicKey = (group) => (group.epic_issue_id == null ? 'none' : String(group.epic_issue_id));

  const epicGroups = useMemo(() => {
    if (!draftsData) return [];
    let groups = [];
    if (draftsData.epicTree && draftsData.epicTree.length > 0) {
      groups = draftsData.epicTree;
    } else if (draftsData.tree && draftsData.tree.length > 0) {
      groups = [{ epic_issue_id: null, epic_title: 'AI drafts', stories: draftsData.tree }];
    } else {
      return [];
    }
    if (!focusEpicId) return groups;
    const want = String(focusEpicId);
    const filtered = groups.filter((g) => g.epic_issue_id != null && String(g.epic_issue_id) === want);
    return filtered.length > 0 ? filtered : groups;
  }, [draftsData, focusEpicId]);

  useEffect(() => {
    if (!epicGroups.length) return;
    if (focusEpicId) {
      setExpandedEpics(new Set([String(focusEpicId)]));
      return;
    }
    setExpandedEpics(new Set(epicGroups.map((g) => epicKey(g))));
  }, [epicGroups, focusEpicId]);

  const toggleEpic = (key) => {
    setExpandedEpics((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleStory = (storyId) => {
    setExpandedStories(prev => {
      const next = new Set(prev);
      if (next.has(storyId)) next.delete(storyId);
      else next.add(storyId);
      return next;
    });
  };

  const toggleSelect = (id) => {
    setSelectedItems(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Toggle parent story and all its child tasks
  const toggleStorySelect = (story) => {
    setSelectedItems(prev => {
      const next = new Set(prev);
      const storySelected = next.has(story.id);
      
      // Get all child task IDs that are pending
      const childTaskIds = (story.tasks || [])
        .filter(task => task.status === 'pending')
        .map(task => task.id);
      
      if (storySelected) {
        // Deselect story and all child tasks
        next.delete(story.id);
        childTaskIds.forEach(id => next.delete(id));
      } else {
        // Select story and all child tasks (only if story is pending)
        if (story.status === 'pending') {
          next.add(story.id);
        }
        childTaskIds.forEach(id => next.add(id));
      }
      
      return next;
    });
  };

  const selectAll = () => {
    const pending = (draftsData?.flat || []).filter(d => d.status === 'pending').map(d => d.id);
    setSelectedItems(new Set(pending));
  };

  const deselectAll = () => setSelectedItems(new Set());

  const handleBulkAction = (action) => {
    if (selectedItems.size === 0) {
      toast.error('No items selected');
      return;
    }
    bulkMutation.mutate({ ids: Array.from(selectedItems), action });
  };

  if (draftsLoading) {
    return (
      <div style={{ padding: '3rem', textAlign: 'center', color: '#64748b' }}>
        Loading AI drafts...
      </div>
    );
  }

  const stats = {
    total: draftsData?.total || 0,
    pending: draftsData?.pending || 0,
    approved: draftsData?.approved || 0,
    rejected: draftsData?.rejected || 0,
  };

  return (
    <div style={{ padding: '1.5rem 2rem', maxWidth: 1200, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 8 }}>
          <button
            onClick={() => navigate('/sprint-board')}
            style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: '0.85rem', padding: 0 }}
          >
            ← Back to Sprint Board
          </button>
          {isManager && stats.total > 0 && (
            <button
              onClick={handleClearAll}
              disabled={clearAllMutation.isPending}
              style={{
                background: '#fee2e2', border: '1px solid #fecaca', color: '#dc2626',
                padding: '4px 10px', borderRadius: 6, fontSize: '0.75rem', cursor: 'pointer',
                fontWeight: 600
              }}
            >
              🗑️ Clear All Drafts
            </button>
          )}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 800, color: '#1e293b' }}>
              🤖 AI-Generated Tasks
            </h1>
            <p style={{ margin: '0.25rem 0 0', color: '#64748b', fontSize: '0.9rem' }}>
              {sprint?.sprint_name || `Sprint #${sprintId}`} — Review and approve AI suggestions
              {focusEpicId ? (
                <span style={{ display: 'block', marginTop: 6, fontSize: '0.82rem', fontWeight: 600, color: '#4f46e8' }}>
                  Showing drafts for Epic #{focusEpicId} (from Smart Planning)
                </span>
              ) : null}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button
              onClick={() => setShowHealth(!showHealth)}
              style={{
                padding: '8px 14px', borderRadius: 8, border: '1px solid #e2e8f0',
                background: showHealth ? '#f0fdf4' : '#fff', color: showHealth ? '#16a34a' : '#64748b',
                fontSize: '0.85rem', cursor: 'pointer', fontWeight: 600,
              }}
            >
              {showHealth ? '✓ Health Analysis' : '📊 Sprint Health'}
            </button>
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 16 }}>
        <div style={{ background: '#fef3c7', padding: 16, borderRadius: 8, textAlign: 'center' }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#92400e' }}>{stats.total}</div>
          <div style={{ fontSize: '0.8rem', color: '#78350f' }}>Total Items</div>
        </div>
        <div style={{ background: '#d1fae5', padding: 16, borderRadius: 8, textAlign: 'center' }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#065f46' }}>{stats.approved}</div>
          <div style={{ fontSize: '0.8rem', color: '#047857' }}>Approved</div>
        </div>
        <div style={{ background: '#fee2e2', padding: 16, borderRadius: 8, textAlign: 'center' }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#991b1b' }}>{stats.rejected}</div>
          <div style={{ fontSize: '0.8rem', color: '#b91c1c' }}>Rejected</div>
        </div>
        <div style={{ background: '#EAF6FD', padding: 16, borderRadius: 8, textAlign: 'center' }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1272B3' }}>{stats.pending}</div>
          <div style={{ fontSize: '0.8rem', color: '#2C63AE' }}>Pending Review</div>
        </div>
      </div>

      {/* Sprint Capacity Analysis */}
      {draftsData?.capacity_analysis && (
        <div style={{
          background: draftsData.capacity_analysis.status === 'overloaded' ? '#fef2f2' : 
                       draftsData.capacity_analysis.status === 'underutilized' ? '#fef3c7' : '#f0fdf4',
          border: `2px solid ${draftsData.capacity_analysis.status === 'overloaded' ? '#fca5a5' : 
                                 draftsData.capacity_analysis.status === 'underutilized' ? '#fcd34d' : '#86efac'}`,
          borderRadius: 12,
          padding: 16,
          marginBottom: 16
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
            <div style={{ fontSize: '1.5rem' }}>
              {draftsData.capacity_analysis.status === 'overloaded' ? '⚠️' : 
               draftsData.capacity_analysis.status === 'underutilized' ? '💡' : '✅'}
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#1e293b' }}>
                Sprint Capacity Analysis
              </div>
              <div style={{ fontSize: '0.85rem', color: '#64748b', marginTop: 2 }}>
                {draftsData.capacity_analysis.message}
              </div>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div style={{ background: 'white', padding: 10, borderRadius: 6, textAlign: 'center' }}>
              <div style={{ fontSize: '1.2rem', fontWeight: 700, color: '#2C8FC7' }}>
                {draftsData.capacity_analysis.total_story_points}
              </div>
              <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Story Points</div>
            </div>
            <div style={{ background: 'white', padding: 10, borderRadius: 6, textAlign: 'center' }}>
              <div style={{ fontSize: '1.2rem', fontWeight: 700, color: '#8b5cf6' }}>
                {draftsData.capacity_analysis.estimated_days}
              </div>
              <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Estimated Days</div>
            </div>
            <div style={{ background: 'white', padding: 10, borderRadius: 6, textAlign: 'center' }}>
              <div style={{ fontSize: '1.2rem', fontWeight: 700, color: '#64748b' }}>
                {draftsData.capacity_analysis.typical_sprint_days}
              </div>
              <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Sprint Days</div>
            </div>
          </div>
          {draftsData.capacity_analysis.suggestions && draftsData.capacity_analysis.suggestions.length > 0 && (
            <div style={{ background: 'white', padding: 12, borderRadius: 6 }}>
              <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#1e293b', marginBottom: 6 }}>
                💡 Suggestions:
              </div>
              <ul style={{ margin: 0, paddingLeft: 20, fontSize: '0.8rem', color: '#64748b' }}>
                {draftsData.capacity_analysis.suggestions.map((suggestion, idx) => (
                  <li key={idx} style={{ marginBottom: 4 }}>{suggestion}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Health Analysis Panel */}
      {showHealth && (
        <div style={{
          background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0',
          padding: '1.25rem', marginBottom: '1.25rem',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#1e293b' }}>
              📊 AI Sprint Health Analysis
            </h3>
            <button
              onClick={() => refetchHealth()}
              disabled={healthLoading}
              style={{
                padding: '6px 12px', borderRadius: 6, border: '1px solid #e2e8f0',
                background: '#f8fafc', color: '#64748b', fontSize: '0.78rem', cursor: 'pointer',
              }}
            >
              {healthLoading ? 'Analyzing...' : '↻ Refresh'}
            </button>
          </div>
          {healthLoading ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
              🤖 AI is analyzing sprint health...
            </div>
          ) : healthData?.analysis ? (
            <div>
              <div style={{
                display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap',
              }}>
                <div style={{
                  padding: '10px 16px', borderRadius: 8,
                  background: healthData.analysis.overallStatus === 'On Track' ? '#dcfce7' :
                              healthData.analysis.overallStatus === 'At Risk' ? '#fef3c7' : '#fee2e2',
                  border: `1px solid ${healthData.analysis.overallStatus === 'On Track' ? '#86efac' :
                                        healthData.analysis.overallStatus === 'At Risk' ? '#fde68a' : '#fecaca'}`,
                }}>
                  <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Status</div>
                  <div style={{ fontSize: '1rem', fontWeight: 700, color: '#1e293b' }}>
                    {healthData.analysis.overallStatus}
                  </div>
                </div>
                <div style={{ padding: '10px 16px', borderRadius: 8, background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                  <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Health Score</div>
                  <div style={{ fontSize: '1rem', fontWeight: 700, color: '#1e293b' }}>
                    {healthData.analysis.healthScore}%
                  </div>
                </div>
              </div>
              <p style={{ fontSize: '0.9rem', color: '#374151', lineHeight: 1.6, margin: '0 0 1rem' }}>
                {healthData.analysis.summary}
              </p>
              {healthData.analysis.insights?.length > 0 && (
                <div style={{ marginTop: '1rem' }}>
                  <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#1e293b', marginBottom: 8 }}>
                    Insights
                  </div>
                  {healthData.analysis.insights.map((insight, i) => (
                    <div key={i} style={{
                      padding: '10px 12px', marginBottom: 8, borderRadius: 8,
                      background: insight.type === 'critical' ? '#fee2e2' :
                                  insight.type === 'warning' ? '#fef3c7' :
                                  insight.type === 'positive' ? '#dcfce7' : '#f8fafc',
                      border: `1px solid ${insight.type === 'critical' ? '#fecaca' :
                                            insight.type === 'warning' ? '#fde68a' :
                                            insight.type === 'positive' ? '#86efac' : '#e2e8f0'}`,
                    }}>
                      <div style={{ fontWeight: 600, fontSize: '0.85rem', color: '#1e293b' }}>
                        {insight.title}
                      </div>
                      <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: 2 }}>
                        {insight.description}
                      </div>
                      {insight.action && (
                        <div style={{ fontSize: '0.78rem', color: '#2C8FC7', marginTop: 4, fontWeight: 500 }}>
                          → {insight.action}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '1rem', color: '#94a3b8' }}>
              Click refresh to generate health analysis
            </div>
          )}
        </div>
      )}

      {/* Bulk Actions */}
      {isManager && stats.pending > 0 && (
        <div style={{
          display: 'flex', gap: 10, marginBottom: '1rem', alignItems: 'center',
          padding: '10px 14px', background: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0',
        }}>
          <span style={{ fontSize: '0.85rem', color: '#64748b' }}>
            {selectedItems.size} selected
          </span>
          <button onClick={selectAll} style={actionBtnStyle('#64748b')}>Select All</button>
          <button onClick={deselectAll} style={actionBtnStyle('#64748b')}>Deselect</button>
          <div style={{ flex: 1 }} />
          <button
            onClick={() => handleBulkAction('approve')}
            disabled={bulkMutation.isPending || selectedItems.size === 0}
            style={actionBtnStyle('#22c55e', true)}
          >
            ✓ Approve Selected
          </button>
          <button
            onClick={() => handleBulkAction('reject')}
            disabled={bulkMutation.isPending || selectedItems.size === 0}
            style={actionBtnStyle('#ef4444', true)}
          >
            ✕ Reject Selected
          </button>
        </div>
      )}

      {/* No Items */}
      {epicGroups.length === 0 && (
        <div style={{
          textAlign: 'center', padding: '4rem 2rem', background: '#fff',
          borderRadius: 12, border: '1px solid #e2e8f0',
        }}>
          <div style={{ fontSize: '3rem' }}>🤖</div>
          <div style={{ fontSize: '1.1rem', fontWeight: 600, color: '#64748b', marginTop: '0.75rem' }}>
            No AI-generated items for this sprint
          </div>
          <div style={{ fontSize: '0.9rem', color: '#94a3b8', marginTop: 4 }}>
            Upload a BRD document when creating a sprint to generate tasks
          </div>
        </div>
      )}

      {/* Epic groups → stories → tasks */}
      {epicGroups.map((group) => {
        const ek = epicKey(group);
        const epicOpen = expandedEpics.has(ek);
        return (
          <div
            key={ek}
            style={{
              marginBottom: '1.25rem',
              borderRadius: 14,
              border: `2px solid ${ITEM_COLORS.epic.border}`,
              overflow: 'hidden',
              background: '#fff',
            }}
          >
            <div
              role="button"
              tabIndex={0}
              onClick={() => toggleEpic(ek)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleEpic(ek); } }}
              style={{
                padding: '12px 16px',
                background: ITEM_COLORS.epic.bg,
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                cursor: 'pointer',
                borderBottom: epicOpen ? `1px solid ${ITEM_COLORS.epic.border}` : 'none',
              }}
            >
              <span style={{ fontSize: '1.1rem', color: ITEM_COLORS.epic.text }}>{epicOpen ? '▼' : '▶'}</span>
              <span style={{
                fontSize: '0.7rem', fontWeight: 800, padding: '3px 8px', borderRadius: 4,
                background: ITEM_COLORS.epic.text, color: '#fff', letterSpacing: '0.04em',
              }}>EPIC</span>
              <span style={{ fontWeight: 800, fontSize: '1rem', color: '#1e293b', flex: 1 }}>{group.epic_title}</span>
              <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                {group.stories.length} stor{group.stories.length === 1 ? 'y' : 'ies'}
              </span>
            </div>
            {epicOpen && group.stories.map((story) => (
        <div key={story.id} style={{
          margin: 0, background: '#fff', borderRadius: 0,
          borderBottom: `1px solid ${ITEM_COLORS.story.border}`,
          borderTop: 'none',
        }}>
          {/* Story Header */}
          <div
            style={{
              padding: '14px 16px', background: ITEM_COLORS.story.bg,
              display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer',
            }}
            onClick={() => toggleStory(story.id)}
          >
            {isManager && story.status === 'pending' && (
              <input
                type="checkbox"
                checked={selectedItems.has(story.id)}
                onChange={(e) => { e.stopPropagation(); toggleStorySelect(story); }}
                style={{ width: 18, height: 18, cursor: 'pointer' }}
              />
            )}
            <span style={{ fontSize: '1.2rem' }}>{expandedStories.has(story.id) ? '▼' : '▶'}</span>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{
                  fontSize: '0.7rem', fontWeight: 700, padding: '2px 8px', borderRadius: 4,
                  background: ITEM_COLORS.story.text, color: '#fff',
                }}>STORY</span>
                <span style={{ fontWeight: 700, fontSize: '1rem', color: '#1e293b' }}>{story.title}</span>
                <StatusBadge status={story.status} />
              </div>
              {story.description && (
                <div style={{ fontSize: '0.85rem', color: '#64748b', marginTop: 4 }}>{story.description}</div>
              )}
            </div>
            {isManager && story.status === 'pending' && (
              <div style={{ display: 'flex', gap: 6 }} onClick={e => e.stopPropagation()}>
                <button
                  onClick={() => approveMutation.mutate(story.id)}
                  disabled={approveMutation.isPending}
                  style={itemBtnStyle('#22c55e')}
                >✓</button>
                <button
                  onClick={() => rejectMutation.mutate(story.id)}
                  disabled={rejectMutation.isPending}
                  style={itemBtnStyle('#ef4444')}
                >✕</button>
              </div>
            )}
          </div>

          {/* Tasks */}
          {expandedStories.has(story.id) && (
            <div>
              {story.tasks?.map(task => (
                <div key={task.id} style={{
                  padding: '10px 16px 10px 72px', background: ITEM_COLORS.task.bg,
                  display: 'flex', alignItems: 'center', gap: 10,
                  borderTop: '1px solid #e2e8f0',
                }}>
                  {isManager && task.status === 'pending' && (
                    <input
                      type="checkbox"
                      checked={selectedItems.has(task.id)}
                      onChange={() => toggleSelect(task.id)}
                      style={{ width: 14, height: 14, cursor: 'pointer' }}
                    />
                  )}
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      <span style={{
                        fontSize: '0.6rem', fontWeight: 700, padding: '1px 5px', borderRadius: 3,
                        background: ITEM_COLORS.task.text, color: '#fff',
                      }}>TASK</span>
                      <span style={{ fontWeight: 500, fontSize: '0.85rem', color: '#1e293b' }}>{task.title}</span>
                      <StatusBadge status={task.status} small />
                      {task.story_points && (
                        <span style={{
                          fontSize: '0.65rem', fontWeight: 600, padding: '1px 5px', borderRadius: 8,
                          background: '#EAF6FD', color: '#2C8FC7',
                        }}>{task.story_points} pts</span>
                      )}
                      {task.suggested_team && (
                        <span style={{
                          fontSize: '0.6rem', padding: '1px 5px', borderRadius: 3,
                          background: '#f1f5f9', color: '#64748b',
                        }}>{task.suggested_team}</span>
                      )}
                    </div>
                  </div>
                  {isManager && task.status === 'pending' && (
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button
                        onClick={() => approveMutation.mutate(task.id)}
                        disabled={approveMutation.isPending}
                        style={itemBtnStyle('#22c55e', true)}
                      >✓</button>
                      <button
                        onClick={() => rejectMutation.mutate(task.id)}
                        disabled={rejectMutation.isPending}
                        style={itemBtnStyle('#ef4444', true)}
                      >✕</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
            ))}
          </div>
        );
      })}
    </div>
  );
};

// Helper Components
const StatusBadge = ({ status, small }) => {
  const colors = {
    pending: { bg: '#fef3c7', color: '#d97706' },
    approved: { bg: '#dcfce7', color: '#16a34a' },
    rejected: { bg: '#fee2e2', color: '#dc2626' },
  };
  const c = colors[status] || colors.pending;
  return (
    <span style={{
      fontSize: small ? '0.6rem' : '0.65rem', fontWeight: 600,
      padding: small ? '1px 4px' : '2px 6px', borderRadius: 4,
      background: c.bg, color: c.color, textTransform: 'capitalize',
    }}>
      {status}
    </span>
  );
};

const actionBtnStyle = (color, filled) => ({
  padding: '6px 12px', borderRadius: 6,
  border: `1px solid ${color}`,
  background: filled ? color : 'transparent',
  color: filled ? '#fff' : color,
  fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer',
});

const itemBtnStyle = (color, small) => ({
  width: small ? 24 : 28, height: small ? 24 : 28,
  borderRadius: 6, border: `1px solid ${color}`,
  background: '#fff', color: color,
  fontSize: small ? '0.75rem' : '0.85rem', fontWeight: 700,
  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
});

export default AIDraftReview;
